import { createLogger } from 'shared/services/logger';
import us from './../../defines';
import {isLightColor, toHexColor, isUnDefined, isDefined, insertAt, removeChunk} from '../../lib/utility/helpers';
import {Point} from '../../lib/objects/';
import Region from './regions/region';
import {renderArrow} from './drawings/arrow';
import {translate} from 'shared/services/I18nService';
const logger = createLogger('UScope:extensions:annotdraw');
const i18Namespace = 'uScope.extensions.annotations.draw';
// ________________________________________________________________________________________________
// annotdraw.js
//    This file contains functions performing annotations drawing and rapahel SVG graphics calls.
//        All are prototypes of AnnotationsService.
//
//    references:
//        raphael.js is used to draw the vector graphics: http://raphaeljs.com
//        Scalable Vector Graphics: http://www.w3.org/TR/SVG/    http://www.w3.org/TR/SVG/Overview.html
//        SVG attribute index: http://www.w3.org/TR/SVG/attindex.html
//        Future options: http://www.graphdracula.net/

// This file used to contain drawing functions as globals. Since annotations service is already fairly overloaded
// left these functions here and just decorated the annotations service with them to improve encapsulation.

export function addDrawFunctions (uScope, viewport, annotationsService, textEditPanel, userConfig) {
  var self = annotationsService;
  var CANNOT_DRAW = annotationsService;
  var CANNOT_REGION = annotationsService;

  // ............................................ startNew
  //    AnnotationsService.startNew - This function begins the drawing of a new annotation object.
  //
  //    parameters:
  //        anType - us.eAnTypes type, annotation type to be created
  //        crsrPos    - Point type, cursor position in base image coords
  //        viewZoom - numeric type, zoom of current view
  //        optText - optional, content for text annotation
  //
  CANNOT_DRAW.startNew = function (anType, crsrPos, viewZoom, viewAngleDeg, optText) {
    const $t = translate.scope(i18Namespace);

    var myLayerId = self.chooseLayer(anType); // get the annotation layer this region will belong to
    if (myLayerId < 0 || !myLayerId) { // we cannot send an addRegion request to dataserver without an assigned layer id
      logger.error('annotdraw::startNew() - Layer not yet created or drawing is on an output layer w/o an input layer - Prohibiting region creation');
      return;
    }

    var isReportRegion = (anType === us.eAnTypes.report);
    this.newRegion = initializeRegion.call(this); // create a new region object
    var newRegion = this.newRegion; // and a handy ref

    var MINANNOTDIM = us.annotSpecs.minAnnotDim;

    switch (newRegion.shape) {
      case 'pen':
      case 'negpen':
        newRegion.addPt(crsrPos);
        self.rfDraw(this.paper, newRegion);
        break;
      case 'caliper':
      case 'polygon':
        newRegion.addPt(crsrPos);
        newRegion.data.lengthPixels = 0;
        newRegion.data.lengthMicrons = 0;
        self.rfDraw(this.paper, newRegion);
        newRegion.addPt(crsrPos); // add the same pt again so update has one to remove
        break;
      case 'report': // only fixed resolution report regions filter through here
        newRegion.addPt(crsrPos);
        self.rfDraw(this.paper, newRegion);

        if (isReportFixedDim(newRegion.reportType)) { // fixed dimension report regions require only a single point
          this.finishNew();
        }
        break;
      case 'rect':
        newRegion.addPt(crsrPos); // top left
        var x1 = crsrPos.x + MINANNOTDIM;
        var y1 = crsrPos.y + MINANNOTDIM;

        var pt = new Point(x1, crsrPos.y);
        newRegion.addPt(pt); // top right
        pt = new Point(x1, y1);
        newRegion.addPt(pt); // bottom right
        pt = new Point(crsrPos.x, y1);
        newRegion.addPt(pt); // bottom left

        if (isReportRegion == false && newRegion.angleDeg != 0) { // rect is squared with the view frame so unrotate it
          var ptList = newRegion.getPts();
          var cpOrigin = new Point(ptList[0].x, ptList[0].y);
          newRegion.rotatePtsAbout(-newRegion.angleDeg, cpOrigin, 0);
        }
        self.rfDraw(this.paper, newRegion, viewZoom, newRegion.angleDeg);
        break;
      case 'arrow':
      case 'ellipse':
        newRegion.addPt(crsrPos); // top right
        pt = new Point(crsrPos.x + MINANNOTDIM, crsrPos.y + MINANNOTDIM);
        newRegion.addPt(pt); // bottom left
        self.rfDraw(this.paper, newRegion);
        break;
      case 'ruler': // the ruler is never created by uScope; only by ImageScope or WebScope - nothing to do
        return;
      case 'pin':
        newRegion.addPt(crsrPos);
        self.rfDraw(this.paper, newRegion, viewZoom, viewAngleDeg);
        this.finishNew();
        break;
      case 'text':
        if (isUnDefined(optText)) { // sanity check that there is text to write
          this.drawingCompleted(); // if not, Signal The End of Drawing
          return; // and return
        }
        newRegion.note = optText; // add the text to the region's data
        newRegion.addPt(crsrPos);
        self.rfDraw(this.paper, newRegion);
        this.finishNew();
        break;
      default:
        break;
    }

    // ..................................................
    // closure functions
    // ............................................ initializeRegion
    function initializeRegion () {
      var layer = self.layers.getLayerById(myLayerId);

      var newRegion = new Region({
        layerId: myLayerId,
        decBGR: layer.get('LineColor'), // region color is the layer color
        type: anType,
        angleDeg: viewAngleDeg,
        zoom: viewZoom,
        isReportRegion: isReportRegion
      },
      'json',
      self.getMyImage(),
      layer,
      self);

      // openShape is a flag for rfDraw, when true shape is not closed; override default setting at draw start - don't close these shapes during draw
      newRegion.set('openShape', (anType === us.eAnTypes.pen || anType === us.eAnTypes.negpen || anType === us.eAnTypes.caliper || anType === us.eAnTypes.polygon));

      // Note: There are 4 report region styles. The fixed size report regions are handled as report regions
      //            and the user drawn report regions are switched to rects for drawing.
      if (isReportRegion) {
        newRegion.reportType = userConfig.get('reportRegionType'); // save report region type here for convenience
        newRegion.note = $t('report-image'); // report region on-image text

        if (!isReportFixedDim(newRegion.reportType)) {
          newRegion.setType(us.eAnTypes.rect); // if user drawn report then switch to a rect draw
        }
        if (isReportFullRes(newRegion.reportType)) {
          newRegion.viewZoom = viewZoom; // save special data to draw at this zoom
          newRegion.zoom = this.getMyImage().getNormalizationFactor(); // report region overrides viewZoom  when full resolution preference is set
        }
      }
      return newRegion;
    }

    // ..................................................
  };

  // ............................................ updateNew
  //    AnnotationsService.updateNew - This function updates with a new cursor position the drawing of a new annotation process.
  //        The caliper needs the mouse event.
  //
  CANNOT_DRAW.updateNew = function (crsrPos, mouseAction) {
    if (this.drawingIsStarted() == false) return; // if called before startNew, return
    var newRegion = this.newRegion;
    switch (newRegion.shape) {
      case 'pen':
      case 'negpen':
        if (newRegion.getPtsLen() < 1) return; // sanity
        var lastPt = newRegion.getPt(newRegion.getPtsLen() - 1);
        var dist = lastPt.cityBlockDistance(crsrPos);
        if (dist > us.annotSpecs.minPenPointDist) { // don't add points that are too close together
          newRegion.addPt(crsrPos);
          self.rfDraw(this.paper, newRegion);
        }
        break;
      case 'caliper':
      case 'polygon':
        if (newRegion.getPtsLen() < 1) return; // sanity
        if (mouseAction === 'mousemove') { // mouse move updates only if move is sufficiently different
          lastPt = newRegion.getPt(newRegion.getPtsLen() - 1); // otherwise the control is sticky, unresponsive
          dist = lastPt.cityBlockDistance(crsrPos);
          if (dist <= us.annotSpecs.minPenPointDist) {
            return;
          }
        }
        newRegion.setPt(crsrPos, newRegion.getPtsLen() - 1); // overwrite prevous point
        newRegion.computeLength();
        self.rfDraw(this.paper, newRegion); // draw the region

        if (mouseAction === 'mousedown') { // mousedown duplicates the end point in anticipation of the next call's overwrite
          newRegion.addPt(crsrPos);
        }
        break;
      case 'rect':
        if (newRegion.getPtsLen() < 4) return; // sanity
        var ptList = newRegion.getPts();

        if (newRegion.angleDeg != 0) { // if the view is rotated, rotate the cursor (indicating rect RB coord) about the LT of the rect
          var cpOrigin = new Point(ptList[0].x, ptList[0].y);
          crsrPos.rotateAbout(newRegion.angleDeg, cpOrigin);
          crsrPos.toInt();
        }

        ptList[2] = crsrPos; // define the 4 corners of the rect
        ptList[1].x = ptList[2].x;
        ptList[1].y = ptList[0].y;
        ptList[3].x = ptList[0].x;
        ptList[3].y = ptList[2].y;
        enforceDims_(); // force a minimim dimension

        if (newRegion.isReportRegion == false && newRegion.angleDeg != 0) { // rect is squared with the view frame so unrotate it
          cpOrigin = new Point(ptList[0].x, ptList[0].y);
          newRegion.rotatePtsAbout(-newRegion.angleDeg, cpOrigin, 0);
        }
        self.rfDraw(this.paper, newRegion);
        break;
      case 'arrow':
      case 'ellipse':
        if (newRegion.getPtsLen() < 2) return; // sanity
        ptList = newRegion.getPts();
        ptList[1] = crsrPos;
        enforceDims_(); // force a minimim dimension
        self.rfDraw(this.paper, newRegion);
        break;
      case 'report':
        if (isReportFixedDim()) {
          newRegion.setPt(crsrPos, 0); // overwrite prevous left-top point
        } else {
          // act like a rectangle at this
        }
        self.rfDraw(this.paper, newRegion);
        break;
      case 'ruler': // note: rulers are not created in this viewer - the caliper replaces it.
      case 'pin': // no updates are necessary for these annotation types

        break;
      default:
        break;
    }

    // ..................................................
    // closure functions
    // ............................................ enforceDims_
    //    This function insures that the user did not shrink the new region shape beyond the minimum dimensions
    function enforceDims_ () {
      var ptList = newRegion.getPts();
      var MINANNOTDIM = us.annotSpecs.minAnnotDim;
      switch (newRegion.shape) {
        case 'rect':
          if (ptList[2].x < ptList[0].x + MINANNOTDIM) ptList[2].x = ptList[0].x + MINANNOTDIM; // bottom right
          if (ptList[2].y < ptList[0].y + MINANNOTDIM) ptList[2].y = ptList[0].y + MINANNOTDIM;
          ptList[1].x = ptList[2].x;
          ptList[1].y = ptList[0].y; // top right
          ptList[3].x = ptList[0].x;
          ptList[3].y = ptList[2].y; // bottom left
          break;
        case 'ellipse':
          // if somehow the dimension is < minimum allowed, fix it
          if (ptList[1].x < ptList[0].x + MINANNOTDIM) ptList[1].x = ptList[0].x + MINANNOTDIM; // bottom right
          if (ptList[1].y < ptList[0].y + MINANNOTDIM) ptList[1].y = ptList[0].y + MINANNOTDIM;
          break;
        default:
          break;
      }
    }

    // ..................................................
  };

  // ............................................ finishNew
  //    AnnotationsService.finishNew - This function completes the drawing of a new annotation object.
  //                Some annotations are checked if they are of reasonable size and removed rather
  //                than keep a silly user mistake.
  //    returns: true if annotation was saved; false otherwise
  CANNOT_DRAW.finishNew = function () {
    if (this.drawingIsStarted() == false) { // if called before startNew, return
      logger.error('AnnotationsService.finishNew: called but drawing is not started!');
      return false;
    }
    var newRegion = this.newRegion;
    var bDisqualified = false;
    switch (newRegion.shape) {
      case 'pen':
      case 'negpen':
        if (newRegion.getPtsLen() < 3) { // don't save freeforms with less than 3 points
          bDisqualified = true;
          logger.warn('Not saving freeform annotation: too few points');
        } else {
          self.rfDraw(this.paper, newRegion); // so perform the draw to complete the outline
        }
        break;
      case 'caliper':
      case 'polygon':
        newRegion.removeDuplicatePtsAtEnd();

        if (newRegion.getPtsLen() < 2) { // don't save caliper with less than 2 points
          logger.warn('Not saving caliper/polygon annotation: only 1 point');
          bDisqualified = true;
        } else if (newRegion.getPtsLen() < 3 && newRegion.shape == 'polygon') {
          logger.warn('Not saving polygon annotation: only 2 points');
          bDisqualified = true;
        } else {
          newRegion.computeLength();
          if (newRegion.data.lengthMicrons < 3) { // don't save caliper with very small measurement
            logger.warn('Not saving caliper/polygon annotation: too small');
            bDisqualified = true;
          }
          if (bDisqualified == false && newRegion.shape == 'polygon') {
            newRegion.setLastPtToFirstPt(); // since when I read a polygon back it is a pen shape, close the shape by setting the last point to the first
            self.rfDraw(this.paper, newRegion); // perform the draw to display the closed polygon shape
          }
        }
        break;
      case 'report':
      case 'rect':
        if (newRegion.isReportRegion) {
          newRegion.setType(us.eAnTypes.rect); // report region is always a rectangle, switch types now
          self.rfDraw(this.paper, newRegion, newRegion.viewZoom); // redraw to show the report's associated text
          newRegion.viewZoom = undefined; // remove special data
        }
        break;
    }

    if (!bDisqualified) { // if a region is qualified
      this.regAdd(newRegion, true, true); // add the new region to the collection and save back to the db
    } else { // otherwise ...
      newRegion.removeGraphics();
    }

    this.drawingCompleted(); // finishNew: return to not-drawing state
    return bDisqualified;
  };

  CANNOT_DRAW.createTextAt = (annotationsService, location) => {
    const $t = translate.scope(i18Namespace);
    textEditPanel.openTextEditPanel($t('add-on-image-text'), '', annotationsService.onAddImageText, location);
  };

  // ............................................ regReDraw
  // regReDraw - This function modifies the outline of a freeform annotation.
  //                It returns false if drawing is complete or cannot start.
  CANNOT_REGION.regReDraw = function (phase, crsrPos, opt_viewZoom) {
    const $t = translate.scope(i18Namespace);
    var closestIdx;
    var thinTolerance_ = us.reDrawSpecs.thinTolerance; // initialize tolerances
    var startTolerance_ = us.reDrawSpecs.startTolerance * ((opt_viewZoom != undefined) ? opt_viewZoom : 1);
    var skipTolerance_ = us.reDrawSpecs.skipTolerance * ((opt_viewZoom != undefined) ? opt_viewZoom : 1);
    var removeTolerance_ = us.reDrawSpecs.removeTolerance * ((opt_viewZoom != undefined) ? opt_viewZoom : 1);

    var region = this.getSelRegion(); // reference the redraw region

    if (phase == 'start') { // ........ start region redraw mode: mouse down in redraw mode
      if (region.shape != 'pen') { // redraw is only valid for pen regions
        this.drawingCompleted(); // redraw: false start
        return false;
      }

      this.reDrawIdx = 0; // the current position in the points array
      this.reDrawCnt = 0;
      this.reDrawPrevPt = undefined; // signal no points changed yet
      region.priorPts = region.copyPts(); // save original point list so it can be restored

      region.thinOutPts(thinTolerance_); // thin point list of this region in preparation for redraw
      let ptList = region.getPts(); // update reference to region points

      var closest = findClosestPt(crsrPos, ptList, startTolerance_);
      closestIdx = closest.idx;
      if (closestIdx != -1) {
        var newPtList = createNewList(ptList, crsrPos, closestIdx);
        region.replacePts(newPtList);

        this.paintSelRegion(us.DONT_HIGHLIGHT); // force a repaint of this region but do not paint the highlight
        this.reDrawCnt++; // increment redraw iteration count
        this.reDrawPrevPt = new Point(crsrPos.x, crsrPos.y); // save the previous point
        return true; // return true indicates a position close enough to the region was clicked
      } else {
        logger.warn(this.reDrawIdx + ' regReDraw: start : not close enough!  dist: ' + closest.dist);
        return false; // return false not starting redraw
      }
    } else if (phase == 'update') { // ........ update region redraw mode: mouse down in redraw mode
      // don't react to new cursor positions that aren't far from the previously inserted point
      if (this.reDrawPrevPt.cityBlockDistance(crsrPos) < skipTolerance_) { return true; }

      let ptList = region.getPts(); // reference to region points

      // find the closest point in list starting at the next index from last insert
      closest = findClosestPtFrom(this.reDrawIdx + 1, ptList, crsrPos); // { idx: idx, dist: closestDist };
      closestIdx = closest.idx;
      if (closestIdx != -1) {
        if (++this.reDrawIdx >= ptList.length - 1) { // if the cursor has lapped back to first point, end drawing
          return false;
        }

        const midpoint = parseInt(ptList.length / 2, 10);
        if ((closestIdx - this.reDrawIdx) > midpoint) { // if the direction of motion is CCW (probably only happens early in the drawing)
          changeListDirection(ptList); // reverse the order of the points in the list

          ptList = region.getPts(); // update reference to region points and the closest point
          closest = findClosestPtFrom(this.reDrawIdx, ptList, crsrPos);
          /// ???? closest = findClosestPtFrom(this.reDrawIdx + 1, ptList, crsrPos);
          closestIdx = closest.idx;
        }

        if (closest.dist < removeTolerance_) { // if it is reasonable to remove array points, do so
          removeChunk(ptList, this.reDrawIdx, closestIdx);
        }

        insertAt(ptList, this.reDrawIdx, crsrPos); // insert this new point right after this.reDrawIdx

        this.paintSelRegion(us.DONT_HIGHLIGHT); // force a repaint of this region but do not paint the highlight
        this.reDrawCnt++; // increment redraw iteration count
        this.reDrawPrevPt = new Point(crsrPos.x, crsrPos.y); // replace the previous point

        return (this.reDrawIdx != ptList.length - 1); // signal drawing finished or can continue
      } else {
        logger.warn(this.reDrawIdx + ' regReDraw: update : not close enough!   dist: ' + closest.dist);
        return false; // return false finishes drawing
      }
    } else if (phase == 'finish') { // ........ finish region redraw mode
      this.drawingCompleted(); // redraw: finshed
      var callbackJson = { anObj: this, region: region };
      if (this.reDrawCnt > 3) { // if the drawing changed enough points in the region
        genericConfirm($t('save-change-confirm'), // then ask user if he wants to save these changes
          $t('save-changes'), regReDrawFinish_cb, callbackJson);
      } else {
        // otherwise draw original region
        regReDrawFinish_cb(callbackJson, false);
      }
      return false;
    } else { // unknown phase passed in, should never get here
      this.drawingCompleted(); // redraw: unknown phase
      return false;
    }

    // ..................................................
    // closure functions
    // ............................................ findClosestPt()
    // findClosestPt - finds the point in the passed list that is closest to the passed point
    //                    and optionally within an  maximum distance
    //                    and optionally can ignore a point at a specific list index
    function findClosestPt (pt, list, opt_tolerance, opt_skipIdx) {
      var idx = -1;
      var closestDist = Number.MAX_VALUE;
      var tolerance = (opt_tolerance) || Number.MAX_VALUE;
      for (var p = 0, len = list.length; p < len; p++) {
        if (p == opt_skipIdx) { // ignore the skip index if it is defined
          continue;
        }
        var adist = list[p].cityBlockDistance(pt); // compute distance to the pt
        if (adist < closestDist) {
          idx = p; // save the list point closest to the passed point
          closestDist = adist;
        }
      }

      if (closestDist < tolerance) {
        return { idx: idx, dist: closestDist };
      } else {
        return { idx: -1, dist: Number.MAX_VALUE };
      }
    }

    // ............................................ findClosestPtFrom()
    //    findClosestPtFrom - finds the point the the passed list starting at the passed start index
    //                        that is closest to the passed point
    function findClosestPtFrom (start, list, pt) {
      var idx = -1;
      var closestDist = Number.MAX_VALUE;
      for (var p = start, len = list.length; p < len; p++) {
        var dist = list[p].cityBlockDistance(pt);
        if (dist < closestDist) {
          idx = p;
          closestDist = dist;
        }
      }
      return { idx: idx, dist: closestDist };
    }

    // ............................................ createNewList()
    //    createNewList - replace or insert into the passed list a new point
    //                    and reorder the list starting at the new point
    function createNewList (ptList, crsrPos, closestIdx) {
      var insertIdx = closestIdx + 1;
      insertAt(ptList, insertIdx, crsrPos);

      var len = ptList.length; // update the new list length
      var newPtList = []; // new, temporary array

      for (var p = 0, o = insertIdx; p < len; p++) { // now put this point at index 0 of list
        newPtList[p] = ptList[o];
        if (++o >= ptList.length) { // wrap index around original list
          o = 0;
        }
      }
      return newPtList;
    }

    // ............................................ changeListDirection()
    function changeListDirection (ptList) {
      // index 0,1,2,3,4....99 ---> 0,99,98,97...
      var item0 = ptList.shift(); // remove 1st list item
      ptList.reverse(); // reverse the list
      ptList.unshift(item0); // insert the saved 1st item
    }

    // ..................................................
  };

  // ............................................ regReDrawFinish_cb
  function regReDrawFinish_cb (json, save) {
    // note: although this is a prototype when called it does not know itself (this is window)
    var anObj = json.anObj;
    var region = json.region;
    if (!save) {
      region.replacePts(region.priorPts); // restore the original region points
    } else { // save this region's points
      if (uScope.canSaveAnnotations()) { // if incremental save is enabled, automatically update the database
        var regionXML = '<Region Id="' + region.regionId + '">';
        var vertexXML = region.buildVertexXML();
        regionXML += '<Vertices>' + vertexXML + '</Vertices>' + '</Region>';
        anObj.save({
          action: 'updateRegion',
          layerId: region.layerId,
          regionXML: regionXML
        });
      }
    }
    anObj.paintSelRegion(); // force a repaint of this region
  }

  // -------------------------------------------------------------------------------------------------
  //    raphael interface functions
  // -------------------------------------------------------------------------------------------------

  // ............................................ rfSelect
  //    rfSelect - This function sets the outline width of the region.
  function rfSelect (aRegion, select) {
    var strokeWidth = select ? us.annotSpecs.strokeWidthSel : aRegion.getStrokeWidth();
    if (aRegion.type != +us.eAnTypes.text) {
      aRegion.rfHandle.attr({ 'stroke-width': strokeWidth });
    } else {
      aRegion.data.textBBoxHandle.attr({ 'stroke-width': strokeWidth });
    }
  }

  // -------------------------------------------------------------------------------------------------
  //    deSaturate -
  // cp-this isn't really desaturating the color ... rename me?
  function deSaturate (decRGB) {
    var blue = (decRGB & 0xFF);
    blue = Math.round(blue / 2);
    blue = blue.toString(16);
    if (blue.length < 2) blue = '0' + blue;

    var green = (decRGB >> 8) & 0xFF;
    green = Math.round(green / 2);
    green = green.toString(16);
    if (green.length < 2) green = '0' + green;

    var red = (decRGB >> 16) & 0xFF;
    red = Math.round(red / 2);
    red = red.toString(16);
    if (red.length < 2) red = '0' + red;

    return (red + green + blue);
  }

  // ............................................ rfDraw
  //    rfDraw - raphael graphics draw - This function draws the specfied shape in its defined position
  //            at scale.  When called with a defined rf handle the graphic's shape is updated,
  //            otherwise a new one is created and the newRegion's rfHandle and rfId are asigned.
  //            It always returns the rf handle to the annotation object.
  //    Notes:
  //        - Vertices of regions are always in base coordinate system and scaled for the current zoom.
  //        - IE8 would fill all regions and outlines with white at opacity 1.0.  To resolve this the
  //            attributes could not assume defaults and had to repeat all settings when updating the graphic.
  //
  CANNOT_REGION.rfDraw = function rfDraw (paper, newRegion, opt_viewZoom, opt_viewAngleDeg) {
    if (!newRegion) {
      return;
    }
    if (!opt_viewAngleDeg) {
      opt_viewAngleDeg = 0;
    }

    // closure vars
    var pad = us.annotSpecs.textBBoxPad;
    var padX2 = pad * 2;
    var decColor = newRegion.decRGB;
    var hexColor = '#' + toHexColor(decColor);
    var hexBackColor = isLightColor(decColor) ? '#000' : '#FFF'; // if forecolor is light, backcolor is dark and visa versa
    var fillColor = '#' + deSaturate(decColor);
    var ptList = newRegion.getPts(); // point list is in base (zoom = 1.0) coords
    var vtxCnt = ptList.length;

    // odd things happen when ending a caliper behind the magnifier, for example
    if (vtxCnt < 1) {
      return;
    }

    // if a opt_viewZoom value is not passed in, draw at the region's zoom value
    var scale = (isDefined(opt_viewZoom)) ? 1.0 / opt_viewZoom : 1.0 / newRegion.zoom;

    var strokeWidth = newRegion.getStrokeWidth(); // select the proper stroke width for this region
    var attrs; // rf draw attributes array

    var rfCurHandle = newRegion.rfHandle;
    switch (newRegion.shape) {
      // path format: "M 100 100 L 300 100 L 200 300" (open path) - we are using this form during draw
      // path format: "M 100 100 L 300 100 L 200 300 Z" (closed path) - we are using this once drawn item is completed
      case 'pen':
      case 'negpen':
        var spath = 'M ' + ptList[0].x * scale + ' ' + ptList[0].y * scale;
        if (vtxCnt > 1) spath += ' L ' + ptList[1].x * scale + ' ' + ptList[1].y * scale;

        for (var v = 2, vlen = vtxCnt; v < vlen; v++) {
          spath += ' ' + ptList[v].x * scale + ' ' + ptList[v].y * scale;
        }
        spath += (newRegion.openShape == false) ? ' Z' : ''; // closepath ?

        var strokeDash = (newRegion.shape == 'negpen') ? '- ' : '';
        var fillOpacity = (newRegion.shape == 'negpen') ? us.annotSpecs.fillOpacity : 0.0;
        attrs = {
          'stroke': hexColor,
          'stroke-width': strokeWidth,
          'fill': fillColor,
          'stroke-dasharray': strokeDash,
          'fill-opacity': fillOpacity
        };
        rfCurHandle = rfDrawPath_();
        break;
      case 'ruler':
      case 'caliper':
      case 'polygon':
        spath = 'M ' + ptList[0].x * scale + ' ' + ptList[0].y * scale;
        if (vtxCnt > 1) { spath += ' L ' + ptList[1].x * scale + ' ' + ptList[1].y * scale; }

        for (v = 2; v < vtxCnt; v++) {
          spath += ' ' + ptList[v].x * scale + ' ' + ptList[v].y * scale;
        }
        spath += (newRegion.openShape == false) ? ' Z' : ''; // closepath ?

        attrs = { 'stroke': hexColor, 'stroke-width': strokeWidth, 'fill': fillColor, 'fill-opacity': 0.0 };
        rfCurHandle = rfDrawPath_();

        if (newRegion.shape == 'ruler' || newRegion.shape == 'caliper') {
          if (newRegion.isHighLight == false) { // if not drawing the outline of the highlight, draw the caliper distance metric
            var xloc = newRegion.getPt(vtxCnt - 1).x * scale - 20;
            var yloc = newRegion.getPt(vtxCnt - 1).y * scale - 25;
            var str = newRegion.data.lengthMicrons + ' um';
            rfDrawRegionText(paper, newRegion, str, xloc, yloc);
          }
        }
        break;
      case 'rect': // note iscope always draws a rect (regardless of the vertex's stored) but, since this is defined with 4 vertices, use path here
        if (vtxCnt < 4) {
          // TODO: maybe warn instead?
          logger.warn(`Shape is rectangle but only ${vtxCnt} points are defined`);
          return rfCurHandle;
        }
        // report region scale is passed in
        if (newRegion.isReportRegion && newRegion.viewZoom) scale = 1.0 / newRegion.viewZoom;

        spath = 'M ' + ptList[0].x * scale + ' ' + ptList[0].y * scale + ' L ' + ptList[1].x * scale + ' ' + ptList[1].y * scale +
          ' ' + ptList[2].x * scale + ' ' + ptList[2].y * scale + ' ' + ptList[3].x * scale + ' ' + ptList[3].y * scale + ' Z';
        attrs = { 'stroke': hexColor, 'stroke-width': strokeWidth, 'fill': fillColor, 'fill-opacity': 0.0 };
        rfCurHandle = rfDrawPath_();

        // for report region (not for other rects with text), draw the on-image text
        if (newRegion.isHighLight == false && newRegion.isReportRegion) {
          if (newRegion.note.length) { // if the note has content, draw it
            var bbox = newRegion.rfHandle.getBBox();
            xloc = newRegion.getPt(2).x * scale;
            yloc = newRegion.getPt(2).y * scale + 15;
            rfDrawRegionText(paper, newRegion, newRegion.note, xloc, yloc);
          } else {
            if (newRegion.data.rfTextHandle) {
              newRegion.data.rfTextHandle.hide(); // if the note does not have content
              newRegion.data.textBBoxHandle.hide(); // hide the text box and text
              newRegion.data = {}; // and clear the associative array
            }
          }
        }
        break;
      case 'report':
        // This handler fires for fixed resolution regions only. In other cases, the report is converted to a rect,
        // and once drawing is finished its textbox is added.
        if (vtxCnt < 1) return rfCurHandle;
        // the following is only good for report types of fixed size, user drawn are drawn as type rect from the start
        if (newRegion.viewZoom) scale = 1 / newRegion.viewZoom;

        // fixed report size but drawn at any resolution requires scaling the fixed report region coords
        var expandToBase;
        if (newRegion.reportType == us.eReportTypes.CurRes_FixedDim) {
          expandToBase = newRegion.zoom;
        } else if (newRegion.reportType == us.eReportTypes.FullRes_FixedDim) {
          expandToBase = viewport.getNormalizationFactor();
        } else {
          expandToBase = 1;
        }

        var width = userConfig.get('reportRegionWidth') * expandToBase;
        var height = userConfig.get('reportRegionHeight') * expandToBase;

        // the first point is to be the center of the image so form the fixed size report rect around it
        ptList[0].x = ptList[0].x - parseInt(width / 2, 10);
        ptList[0].y = ptList[0].y - parseInt(height / 2, 10);
        ptList.length = 1; // insure only top left point in array
        var pt = new Point(ptList[0].x + width, ptList[0].y);
        newRegion.addPt(pt); // top right = ptList[1]
        pt = new Point(ptList[1].x, ptList[0].y + height);
        newRegion.addPt(pt); // bottom right ptList[2]
        pt = new Point(ptList[0].x, ptList[2].y);
        newRegion.addPt(pt); // bottom left = ptList[3]

        ptList = newRegion.getPts(); // now draw the report region at the current zoom
        spath = 'M ' + ptList[0].x * scale + ' ' + ptList[0].y * scale + ' L ' + ptList[1].x * scale + ' ' + ptList[1].y * scale +
          ' ' + ptList[2].x * scale + ' ' + ptList[2].y * scale + ' ' + ptList[3].x * scale + ' ' + ptList[3].y * scale + ' Z';
        attrs = { 'fill': fillColor, 'fill-opacity': 0.0 };
        rfCurHandle = rfDrawPath_();
        break;
      case 'ellipse':
        var left = ptList[0].x * scale; // ptList[0] is top left corner and ptList[1] is bottom right corner
        var top = ptList[0].y * scale;
        var rx = Math.round((ptList[1].x * scale - left + 1) / 2);
        var ry = Math.round((ptList[1].y * scale - top + 1) / 2);
        var cx = Math.round(+left + +rx);
        var cy = Math.round(+top + +ry);

        attrs = { 'stroke': hexColor, 'stroke-width': strokeWidth, 'fill': fillColor, 'fill-opacity': 0.0 };

        if (isDefined(rfCurHandle)) {
          attrs.cx = cx;
          attrs.cy = cy;
          attrs.rx = rx;
          attrs.ry = ry;
          rfCurHandle.attr(attrs);
        } else {
          newRegion.rfHandle = paper.ellipse(cx, cy, rx, ry).attr(attrs);
          newRegion.rfId = newRegion.rfHandle.id;
        }
        break;
      case 'arrow':
        const { svgPath, svgTransformation } = renderArrow(newRegion, ptList, scale, opt_viewZoom);

        attrs = {
          'stroke': hexColor,
          'stroke-width': strokeWidth,
          'fill': hexColor
        };

        if (!newRegion.isHighLight) {
          attrs['fill-opacity'] = 1.0;
        }

        if (rfCurHandle) {
          attrs.path = svgPath;
          rfCurHandle.attr(attrs);
          rfCurHandle.transform(svgTransformation);
        } else {
          newRegion.rfHandle = paper.path(svgPath).attr(attrs);
          newRegion.rfHandle.transform(svgTransformation);
          newRegion.rfId = newRegion.rfHandle.id;
        }

        break;
      case 'pin':
        var coords = us.pinSpecs.coords; // the normalized coord path of the pin drop
        var x = Math.floor(ptList[0].x * scale);
        var y = Math.floor(ptList[0].y * scale);

        spath = 'M ' + (+coords[0] + x) + ' ' + (+coords[1] + y) + ' L'; // build the pin drop path
        for (var p = 2, len = coords.length; p < len; p += 2) {
          spath = spath + ' ' + (+coords[p] + x) + ' ' + (+coords[p + 1] + y);
        }
        spath = spath + ' Z';

        attrs = {
          'stroke': hexColor,
          'stroke-width': strokeWidth,
          'fill': fillColor,
          'fill-opacity': us.annotSpecs.fillOpacity
        };
        rfCurHandle = rfDrawPath_();

        if (!opt_viewAngleDeg) {
          self.rfRotate(newRegion, opt_viewAngleDeg);
        }
        break;
      case 'text':
        if (!newRegion.isHighLight) {
          if (isDefined(rfCurHandle)) { // draw the text
            rfCurHandle.attr({
              fill: hexColor,
              'font-size': us.annotSpecs.fontSize,
              'font-weight': 'bold',
              text: newRegion.note,
              x: ptList[0].x * scale,
              y: ptList[0].y * scale
            });
          } else {
            newRegion.rfHandle = paper.text(ptList[0].x * scale, ptList[0].y * scale, newRegion.note)
              .attr({ fill: hexColor, 'font-size': us.annotSpecs.fontSize, 'font-weight': 'bold' });
            newRegion.rfId = newRegion.rfHandle.id;
          }

          bbox = newRegion.rfHandle.getBBox(); // draw the bounding box around the text
          newRegion.data.bbox = bbox; // save for highlight

          if (isDefined(newRegion.data.textBBoxHandle)) {
            newRegion.data.textBBoxHandle.attr({
              'stroke': hexColor,
              'fill': hexBackColor,
              x: bbox.x - pad,
              y: bbox.y - pad,
              width: bbox.width + padX2,
              height: bbox.height + padX2
            });
          } else {
            newRegion.data.textBBoxHandle = paper.rect(bbox.x - pad, bbox.y - pad, bbox.width + padX2, bbox.height + padX2, us.annotSpecs.textBBoxRadius)
              .attr({ 'stroke': hexColor, 'stroke-width': strokeWidth, fill: hexBackColor });
          }

          newRegion.rfHandle.toFront(); // move the text in front of the bbox
        } else {
          bbox = newRegion.data.bbox;
          if (isDefined(newRegion.rfHandle)) {
            rfCurHandle.attr({
              'stroke': hexColor,
              'stroke-width': strokeWidth,
              'fill-opacity': 0.0,
              x: bbox.x - pad,
              y: bbox.y - pad,
              width: bbox.width + padX2,
              height: bbox.height + padX2
            });
          } else {
            newRegion.rfHandle = paper.rect(bbox.x - pad, bbox.y - pad, bbox.width + padX2, bbox.height + padX2, us.annotSpecs.textBBoxRadius)
              .attr({ 'stroke': hexColor, 'stroke-width': strokeWidth, 'fill-opacity': 0.0 });
          }
        }
        break;
    }

    if (newRegion.isHighLight && newRegion.rfHandle) { // if this region is the high light region
      newRegion.rfHandle.toFront(); // move the highlight in front of the outline
    }

    return newRegion.rfHandle; // return the raphael reference to the graphics object

    // ..................................................
    // closure functions
    // ............................................ rfDrawPath_()
    function rfDrawPath_ () {
      if (isDefined(rfCurHandle)) {
        attrs.path = spath;
        rfCurHandle.attr(attrs);
        return rfCurHandle;
      } else {
        newRegion.rfHandle = paper.path(spath).attr(attrs);
        newRegion.rfId = newRegion.rfHandle.id;
        return newRegion.rfHandle;
      }
    }

    // ............................................ rfDrawRegionText()
    // This function draws text assoicated with a region at the specified location with a white,
    //  round cornered bounding box behind it.  If the raphael objects for the text and its box
    //  do not exist, they are created and the region object data is updated to retain them.
    //    This is used for ruler and caliper annotation.
    function rfDrawRegionText (paper, newRegion, str, xloc, yloc) {
      /// /    var rfDrawRegionText = function (paper, newRegion, str, xloc, yloc) {
      var fontSize = us.annotSpecs.fontSize;
      // draw the text
      if (isDefined(newRegion.data.rfTextHandle)) {
        newRegion.data.rfTextHandle.attr({
          text: str,
          x: xloc,
          y: yloc,
          fill: hexColor,
          'stroke': hexColor,
          'font-size': fontSize
        });
      } else {
        newRegion.data.rfTextHandle = paper.text(xloc, yloc, str)
          .attr({ fill: hexColor, 'stroke': hexColor, 'font-size': fontSize });
      }

      var bbox = newRegion.data.rfTextHandle.getBBox(); // draw the bounding box around the text
      // currently not used newRegion.data.bbox = bbox;                         // save for highlight
      if (isDefined(newRegion.data.textBBoxHandle)) {
        newRegion.data.textBBoxHandle.attr({
          'stroke': hexColor,
          fill: hexBackColor,
          x: bbox.x - pad,
          y: bbox.y - pad,
          width: bbox.width + padX2,
          height: bbox.height + padX2
        });
      } else {
        newRegion.data.textBBoxHandle = paper.rect(bbox.x - pad, bbox.y - pad, bbox.width + padX2, bbox.height + padX2, us.annotSpecs.textBBoxRadius)
          .attr({ 'stroke': hexColor, fill: hexBackColor });
      }
      newRegion.data.rfTextHandle.toFront(); // move the text in front of the bbox
    }
  };

  // ............................................ rfRotate
  //    This function rotates the passed region.
  //    Note that the input angle is negated as uScope's positive direction is the reverse of Raphael's
  CANNOT_REGION.rfRotate = function rfRotate (region, angleDeg) {
    var rfCurHandle = region.rfHandle;
    switch (region.shape) {
      case 'pin':
        rfCurHandle.transform(''); // must clear the transform or the rotation center wanders away!
        var bbox = rfCurHandle.getBBox();
        var xformStr = ' R' + -angleDeg; // absolute rotation angle
        xformStr += ',' + Math.round(bbox.x + bbox.width / 2) + // center of rotation
          ',' + Math.round(bbox.y + bbox.height);
        rfCurHandle.transform(xformStr); // perform the transform
        break;
      default:
        break;
    }
  };

  // ............................................ regHighLight
  // AnnotationsService.regHighLight - This function [de]highlights a region.
  //
  //    aRegion - region to highlight, or not
  //    select - required, {true, false} to highlight or not
  //    viewZoom = optional, view zoom of region to pass to rfDraw
  CANNOT_REGION.regHighLight = function (aRegion, select, opt_viewZoom, opt_viewAngleDeg) {
    var type = +aRegion.type;
    rfSelect(aRegion, select); // increase/decrease the width of the region outline
    if (select) {
      var hiRegion = this.rfRegionHiLite[type]; // get the region that highlights this type

      hiRegion.decRGB = isLightColor(aRegion.decRGB) ? 0 : 16777215; // if forecolor is light, backcolor is dark and visa versa
      hiRegion.zoom = aRegion.zoom;
      hiRegion.pts = aRegion.pts;
      hiRegion.centerPt = aRegion.centerPt;

      if (type == +us.eAnTypes.text) { // for on image text, highlight the bounding box
        hiRegion.data.bbox = aRegion.data.bbox;
      }

      self.rfDraw(this.paper, hiRegion, opt_viewZoom, opt_viewAngleDeg); // draw the highlight
      hiRegion.rfHandle.show();

      if (hiRegion.rfHandle && !hiRegion.hasEventHandlerHighlight) { // if successfully drawn and eventHandle has not been added
        this.addRegEventHandlerHighlight(hiRegion); // add event handler to the highlight element
      }
      if (isDefined(aRegion.data.rfTextHandle)) { // if this region has buddy text, like a caliper measurement
        aRegion.data.rfTextHandle.toFront(); // move the text in front of the region outline so the highlight doesn't cover it
      }
    } else {
      if (this.rfRegionHiLite[type].rfHandle) {
        this.rfRegionHiLite[type].rfHandle.hide(); // if highlight region in use, hide it
      }
    }
  };

  // ............................................ rfDrawHover
  //    rfDrawHover - This function draws the hover note for the passed rfId.
  //                    Note that a note length equal to 0 is valid - erasing the prior note.
  //    Improvements:
  //        -would like to pass region of note and region for hover graphic
  CANNOT_DRAW.rfDrawHover = function (rfId) {
    var rfRegions = this.getRegionsArray();
    var region = rfRegions[rfId];

    if (region.shape === 'text' || region.isReportRegion || // if the region is on-image-text or is report region, prevent show other notes
      isUnDefined(region.note) || region.note.length == 0) { // if the note for this region is not defined or its length is 0
      if (isDefined(this.hoverMemoHandle)) this.hoverMemoHandle.hide();
      if (isDefined(this.hoverMemoBBoxHandle)) this.hoverMemoBBoxHandle.hide();
      return;
    }
    if (isDefined(this.hoverMemoHandle)) this.hoverMemoHandle.show();
    if (isDefined(this.hoverMemoBBoxHandle)) this.hoverMemoBBoxHandle.show();

    var viewZoom = this.getMyImage().getViewZoom();

    var ptList = region.getPts(); // point list is in base (zoom = 1.0) coords
    var xloc = +ptList[0].x / viewZoom;
    var yloc = +ptList[0].y / viewZoom;
    if (region.shape === 'pin') {
      yloc = Math.round(ptList[0].y / viewZoom - us.pinSpecs.height);
    }

    var pad = us.annotSpecs.textBBoxPad;
    var padX2 = pad * 2;
    var decColor = 0; // black text
    var hexColor = '#' + toHexColor(decColor);
    var hexBackColor = '#F6EFBA'; // sticky note yellow
    if (isDefined(this.hoverMemoHandle)) { // draw the text
      this.hoverMemoHandle.attr({ text: region.note, x: xloc, y: yloc, fill: hexColor });
    } else {
      this.hoverMemoHandle = this.paper.text(xloc, yloc, region.note)
        .attr({ 'text-anchor': 'start', fill: hexColor, 'font-size': us.annotSpecs.fontSize });
    }

    var bbox = this.hoverMemoHandle.getBBox();
    if (isDefined(this.hoverMemoBBoxHandle)) { // draw the bounding box around the text
      this.hoverMemoBBoxHandle.attr({
        'stroke': hexColor,
        fill: hexBackColor,
        x: bbox.x - pad,
        y: bbox.y - pad,
        width: bbox.width + padX2,
        height: bbox.height + padX2
      });
    } else {
      this.hoverMemoBBoxHandle = this.paper.rect(bbox.x - pad, bbox.y - pad, bbox.width + padX2, bbox.height + padX2, us.annotSpecs.textBBoxRadius)
        .attr({ 'stroke': hexColor, fill: hexBackColor, opacity: 1.0 });
    }

    yloc = yloc - bbox.height / 2; // adjust the note position for the height of the bounding box
    this.hoverMemoHandle.attr({ y: yloc }).toFront(); // and set the text in front of the bbox

    bbox = this.hoverMemoHandle.getBBox(); // move the bounding box to where the note text moved
    this.hoverMemoBBoxHandle.attr({ y: bbox.y - pad });
  };

  // NOTE: Using loose equality here since the user pref's service doesn't enforce typing yet. TODO: FIX
  const isReportFullRes = reportType => (reportType == us.eReportTypes.FullRes_FixedDim || reportType == us.eReportTypes.FullRes_UserDrawn);

  const isReportFixedDim = reportType => (reportType == us.eReportTypes.FullRes_FixedDim || reportType == us.eReportTypes.CurRes_FixedDim);

  // ............................................ genericConfirm
  //    genericConfirm -
  //
  //    parameters:
  //        message:        required, string, your message to the user
  //        title:            required, string
  //        callback:        required

  /**
   * A general purpose confirm panel that removes itself from the DOM when closed.
   * @param message (string) - your message to the user
   * @param title (string) - title
   * @param callback (function)
   * @param callbackJson
   */
  function genericConfirm (message, title, callback, callbackJson) {
    if (message == undefined) message = '';
    if (title == undefined) title = '';
    if (message.length == 0 && message.length == 0) {
      return;
    }

    var jPanel = $('<div class=' + us.clsPANEL + ' title="' + title + '">' + message + '</div>');

    // append this modal dialog to body to work around a jquery issue that modal dialogs cannot be dismissed in popup mode
    //    (would normally want it appended to the uscope div)
    // note that the dialog is the parent to the panel created
    $('body').append(jPanel);

    jPanel.dialog({
      autoOpen: true,
      modal: true,
      closeOnEscape: false,
      resizable: false,
      dialogClass: 'uscope-dialog dialogMover',
      close: function (event, ui) {
        $(this).remove();
      },
      buttons: [{
        text: 'No',
        click: function () {
          $(this).remove();
          if (callback) {
            callback(callbackJson, false);
          }
          return false;
        }
      }, {
        text: 'Yes',
        click: function () {
          $(this).remove();
          if (callback) {
            callback(callbackJson, true);
          }
          return true;
        }
      }]
    });
  }
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/annotations/annotdraw.js